import React from 'react';

import { IdToken, useAuth0, User } from '@auth0/auth0-react';

import Alerts from '@components/Alerts';
import Footer from '@components/Footer';
import Header from '@components/Header';
import Loading from '@components/Loading';

import useAuth0Hook from '../hooks/useAuth0Hook';

import { NOTIFICATIONS_WRAPPER_ID } from '@domain/notifications';

import {
  FEATURE_AUTH0_LOGIN,
  FEATURE_SITECORE_FOOTER,
  FEATURE_SITECORE_HEADER,
  isEnabled,
} from '@utils/features';

import { useAppConfig } from '@domui-hooks/appConfig';
import SitecoreFooter from '@domui-sitecore/wrappers/SitecoreFooter';
import SitecoreHeader from '@domui-sitecore/wrappers/SitecoreHeader';
import { isProtectedPath } from '@domui-utils/secureRoute';

import ErrorBoundary from '@hooks/error-boundary';
import { log } from '@hooks/logger';

import { LogLevel } from '@typings/graphql';

const Auth0Layout: React.FC = ({ children }) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    user: auth0UserInfo,
    isLoading,
    loginWithRedirect = () => {},
  } = useAuth0();
  const [accessToken, setAccessToken] = React.useState<string>();
  const [idTokenClaims, setIdTokenClaims] = React.useState<
    IdToken | undefined
  >();
  const [auth0User, setAuth0User] = React.useState<User | undefined>();
  const isAuth0LoginEnabled = isEnabled(FEATURE_AUTH0_LOGIN);
  const { language = 'en' } = useAppConfig();
  const auth0AccountSynced = useAuth0Hook(
    accessToken || '',
    idTokenClaims,
    auth0User
  );
  React.useEffect(() => {
    if (isAuth0LoginEnabled) {
      log({
        level: LogLevel.Info,
        message: 'QQ Auth0:: accessToken Fetch Started!',
      });
      if (!isAuthenticated) {
        log({
          level: LogLevel.Info,
          message: `QQ Auth0:: User Not Authenticated Yet!`,
        });
        return; // Wait until user is authenticated
      }

      const fetchData = async () => {
        try {
          const accessToken = await getAccessTokenSilently();
          const idTokenClaims = await getIdTokenClaims();

          if (!accessToken) {
            log({
              level: LogLevel.Info,
              message: 'QQ Auth0:: accessToken not available!',
            });
            return;
          }
          setAccessToken(accessToken);
          setIdTokenClaims(idTokenClaims);
          setAuth0User(auth0UserInfo);
        } catch (err) {
          log({
            level: LogLevel.Error,
            message: `QQ Failed to fetch user info: ${JSON.stringify(err)}`,
          });
        }
      };

      fetchData();
    }
  }, [isAuth0LoginEnabled, isAuthenticated]);

  React.useEffect(() => {
    const pathName =
      typeof window !== 'undefined' ? window.location.pathname : '/';
    const loginRequired =
      isAuth0LoginEnabled && !auth0UserInfo?.email && !isLoading;
    if (isProtectedPath(pathName, language) && loginRequired) {
      loginWithRedirect();
    }
  }, [isLoading, auth0UserInfo?.email, language]);

  if (
    isAuth0LoginEnabled &&
    (isLoading || (isAuthenticated && !auth0AccountSynced))
  ) {
    return <Loading />;
  }

  return (
    <div className="mainContainer min-h-full">
      {isEnabled(FEATURE_SITECORE_HEADER) ? <SitecoreHeader /> : <Header />}
      <Alerts />
      <div id={NOTIFICATIONS_WRAPPER_ID} />
      <main className="flex-1 tablet:pt-1">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      {isEnabled(FEATURE_SITECORE_FOOTER) ? <SitecoreFooter /> : <Footer />}
    </div>
  );
};

export default Auth0Layout;
